import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";
import { useLang } from "@/i18n";

const RED = "#c00000";
const O = { fontFamily: "Outfit, sans-serif" };

export const NAV_LINKS = [
  { de: "Produkte", en: "Products", path: "/produkte" },
  { de: "Lösungen", en: "Solutions", path: "/loesungen" },
  { de: "Branchen", en: "Industries", path: "/branchen" },
  { de: "Ressourcen", en: "Resources", path: "/ressourcen" },
  { de: "Preise", en: "Pricing", path: "/preise" },
  { de: "Kontakt", en: "Contact", path: "/kontakt" },
];

export function SiteLogo({ dark }) {
  return (
    <span className="flex items-center gap-2">
      <svg viewBox="0 0 32 32" className="w-8 h-8" aria-hidden>
        <path d="M5 27 L16 5 L20 5 L11 27 Z" fill={RED} />
        <path d="M14 27 L24 8 L28 8 L20 27 Z" fill={RED} opacity="0.65" />
      </svg>
      <span className={`text-xl font-extrabold tracking-tight ${dark ? "text-white" : "text-gray-900"}`} style={O}>Fleet<span style={{ color: RED }}>Area</span></span>
    </span>
  );
}

export default function SiteNav() {
  const navigate = useNavigate();
  const loc = useLocation();
  const { lang, setLang } = useLang();
  const de = lang === "de";
  const [open, setOpen] = useState(false);
  const go = (p) => { setOpen(false); navigate(p); };

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="site-logo"><SiteLogo /></Link>
        <nav className="hidden lg:flex items-center gap-1 text-sm font-medium">
          {NAV_LINKS.map((n) => {
            const active = loc.pathname === n.path;
            return (
              <button key={n.path} data-testid={`site-nav-${n.path.slice(1)}`} onClick={() => go(n.path)}
                className={`relative px-3 py-2 rounded-lg transition-colors ${active ? "text-gray-900" : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"}`}>
                {de ? n.de : n.en}
                <span className={`absolute left-3 right-3 -bottom-[1px] h-0.5 rounded-full transition-all ${active ? "opacity-100" : "opacity-0"}`} style={{ background: RED }} />
              </button>
            );
          })}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <button data-testid="site-lang-toggle" onClick={() => setLang(de ? "en" : "de")} className="text-xs font-mono px-2.5 py-1.5 rounded-md border border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400 transition-colors">{de ? "EN" : "DE"}</button>
          <button data-testid="site-login" onClick={() => navigate("/login")} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">{de ? "Login" : "Sign in"}</button>
          <button data-testid="site-demo" onClick={() => navigate("/kontakt")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white rounded-lg px-4 py-2 transition-all hover:brightness-110 hover:-translate-y-0.5" style={{ background: RED }}>
            {de ? "Demo anfragen" : "Request demo"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="lg:hidden flex items-center gap-2">
          <button data-testid="site-lang-toggle-m" onClick={() => setLang(de ? "en" : "de")} className="text-xs font-mono px-2 py-1.5 rounded-md border border-gray-300 text-gray-600">{de ? "EN" : "DE"}</button>
          <button className="text-gray-700" onClick={() => setOpen((v) => !v)} data-testid="site-mobile-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-gray-200 px-4 py-4 space-y-1">
          {NAV_LINKS.map((n) => (
            <button key={n.path} onClick={() => go(n.path)} className="block w-full text-left py-2 text-gray-700 hover:text-gray-900 transition-colors">{de ? n.de : n.en}</button>
          ))}
          <button onClick={() => go("/login")} className="w-full mt-2 py-2.5 rounded-lg border border-gray-300 text-gray-800 text-sm font-medium">{de ? "Login" : "Sign in"}</button>
          <button onClick={() => go("/kontakt")} className="w-full py-2.5 rounded-lg text-white text-sm font-semibold" style={{ background: RED }}>{de ? "Demo anfragen" : "Request demo"}</button>
        </div>
      )}
    </header>
  );
}
